import {
  Component,
  signal,
  ChangeDetectionStrategy,
  computed,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule, formatDate } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  TfDataTableComponent,
  TfColumnDefDirective,
  ColumnDef,
  EmptyStateComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
} from '@tourfold/libraries/shared-ui';
import { WebhookDialog } from '../webhook-dialog/webhook-dialog';
import {
  WebhookEndpointsApi,
  WebhookEndpoint,
  CreateWebhookEndpoint,
  UpdateWebhookEndpoint,
  CreateWebhookEndpointSignatureSchemeEnum,
  UpdateWebhookEndpointSignatureSchemeEnum,
  WebhooksCatalogApi,
  WebhookTypeMetadata,
  WebhookTestingApi,
  SendTestWebhook,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { ConfirmationService } from 'primeng/api';
import { firstValueFrom, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { provideWebhooksAiPageContext } from './webhooks-list.ai-page-context';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

// Local interface for event types (since API doesn't provide a list endpoint)
interface WebhookEventType {
  id: string;
  name: string;
  description: string;
  category: string;
}

// Extended endpoint interface for UI state
interface WebhookEndpointWithSubscriptions extends WebhookEndpoint {
  subscriptionIds?: string[];
  invalidSubscriptionIds?: string[];
  lastTestStatus?: 'success' | 'failed' | null;
  lastTestAt?: Date | null;
}

/** Precomputed test health for template (avoids method calls in template). */
interface TestHealthDisplay {
  status: 'success' | 'failed' | null;
  tooltip: string;
}

@Component({
  selector: 'tf-settings-webhooks-list',
  imports: [
    CommonModule,
    ButtonModule,
    TooltipModule,
    ConfirmDialogModule,
    TagModule,
    TranslateModule,
    WebhookDialog,
    EmptyStateComponent,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    TfDataTableComponent,
    TfColumnDefDirective,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './webhooks-list.html',
  styleUrl: './webhooks-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class Webhooks {
  private readonly webhookEndpointsApi = inject(WebhookEndpointsApi);
  private readonly webhooksCatalogApi = inject(WebhooksCatalogApi);
  private readonly webhookTestControllerApi = inject(WebhookTestingApi);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly resourceAccessService = inject(ResourceAccessService);

  readonly canCreateWebhook = computed(() =>
    this.resourceAccessService.canPerform('webhooks', 'create')
  );
  readonly canUpdateWebhook = computed(() =>
    this.resourceAccessService.canPerform('webhooks', 'update')
  );
  readonly canDeleteWebhook = computed(() =>
    this.resourceAccessService.canPerform('webhooks', 'delete')
  );
  readonly canTestWebhook = computed(() =>
    this.resourceAccessService.canPerform('webhooks', 'test')
  );

  readonly columns: ColumnDef[] = [
    { field: 'displayName', title: 'SETTINGS_WEBHOOKS.NAME', type: 'custom', width: '18%' },
    { field: 'endpointUrl', title: 'SETTINGS_WEBHOOKS.URL', type: 'text', width: '25%' },
    { field: 'enabled', title: 'SETTINGS_WEBHOOKS.ENABLED', type: 'custom', width: '8%' },
    { field: 'retry', title: 'SETTINGS_WEBHOOKS.RETRY', type: 'custom', width: '8%' },
    { field: '_eventTypes', title: 'SETTINGS_WEBHOOKS.EVENT_TYPES', type: 'custom', width: '13%' },
    {
      field: '_testStatus',
      title: 'SETTINGS_WEBHOOKS.TEST_WEBHOOK_STATUS',
      type: 'custom',
      width: '8%',
    },
    { field: '_actions', title: 'SETTINGS_WEBHOOKS.ACTIONS', type: 'custom', width: '20%' },
  ];

  readonly endpoints = signal<WebhookEndpointWithSubscriptions[]>([]);
  readonly eventTypes = signal<WebhookEventType[]>([]);
  readonly eventTypesByCategory = signal<Map<string, WebhookEventType[]>>(new Map());
  readonly isLoading = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly isDialogVisible = signal<boolean>(false);
  readonly editingEndpoint = signal<WebhookEndpointWithSubscriptions | null>(null);
  readonly testingEndpointId = signal<string | null>(null);
  readonly showEmptyState = computed(() => this.hasLoadedOnce() && this.endpoints().length === 0);

  readonly eventTypeOptions = computed(() => {
    return this.eventTypes().map((eventType) => ({
      label: `${eventType.name} - ${eventType.category}`,
      value: eventType.id,
      category: eventType.category,
    }));
  });

  /** Per-endpoint test health (status + tooltip) from endpoint.health (list + test response). */
  readonly testHealthByEndpointId = computed(() => {
    const endpoints = this.endpoints();
    const locale = this.translateService.currentLang ?? 'en';
    const lookup = new Map<string, TestHealthDisplay>();
    for (const endpoint of endpoints) {
      lookup.set(endpoint.id, this.deriveTestHealthDisplay(endpoint, locale));
    }
    return lookup;
  });

  /** Per-endpoint event types summary. Template reads this instead of calling methods. */
  readonly eventTypesSummaryByEndpointId = computed(() => {
    const endpoints = this.endpoints();
    const lookup = new Map<string, string>();
    for (const endpoint of endpoints) {
      lookup.set(endpoint.id, this.deriveEventTypesSummary(endpoint));
    }
    return lookup;
  });

  /** Per-endpoint flag indicating invalid subscriptions exist. */
  readonly hasInvalidSubscriptionsByEndpointId = computed(() => {
    const endpoints = this.endpoints();
    const lookup = new Map<string, boolean>();
    for (const endpoint of endpoints) {
      const hasInvalid = (endpoint.invalidSubscriptionIds?.length ?? 0) > 0;
      lookup.set(endpoint.id, hasInvalid);
    }
    return lookup;
  });

  constructor() {
    this.loadEndpoints();
    this.loadEventTypes();
    provideWebhooksAiPageContext({
      endpoints: this.endpoints,
      editingEndpoint: this.editingEndpoint,
      testingEndpointId: this.testingEndpointId,
    });
  }

  private loadEndpoints(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);
    from(this.webhookEndpointsApi.listEndpoints())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((response) => response.webhookEndpoints ?? []),
        map((endpoints) =>
          endpoints.map((e) => {
            return {
              ...e,
              subscriptionIds: e.subscriptions,
              invalidSubscriptionIds: e.invalidSubscriptions ?? [],
            } as WebhookEndpointWithSubscriptions;
          })
        )
      )
      .subscribe({
        next: (endpoints) => {
          this.endpoints.set(endpoints);
          this.isLoading.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const message = this.translateService.instant('SETTINGS_WEBHOOKS.ERROR.LOAD_FAILED');
          if (hadLoadedOnce) {
            this.hasLoadedOnce.set(true);
            this.actionError.set(message);
          } else {
            this.loadError.set(message);
            this.initialLoadFailed.set(true);
            void this.updateLoadErrorDetails(error);
          }
        },
      });
  }

  private loadEventTypes() {
    from(this.webhooksCatalogApi.getCatalog())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((response) => response.webhooks ?? [])
      )
      .subscribe({
        next: (webhooks) => {
          this.processWebhookTypes(webhooks);
        },
        error: () => {
          this.eventTypesByCategory.set(new Map());
          this.eventTypes.set([]);
          this.actionError.set(
            this.translateService.instant('SETTINGS_WEBHOOKS.ERROR.LOAD_CATALOG_FAILED')
          );
        },
      });
  }

  private processWebhookTypes(webhooks: WebhookTypeMetadata[]) {
    const eventTypes: WebhookEventType[] = webhooks.map((metadata) => {
      // Category is derived from the webhook type's leading segment
      // (e.g. "tour.so.created.v1" → "Tour"). The backend response carries no
      // separate category field.
      const parts = metadata.webhookType.split('.');
      const category = parts[0] ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1) : 'Other';

      return {
        id: metadata.webhookType,
        name: metadata.displayName,
        description: metadata.description ?? `Webhook type: ${metadata.webhookType}`,
        category: category,
      };
    });

    // Group by category
    const categories = new Map<string, WebhookEventType[]>();
    eventTypes.forEach((eventType) => {
      const existing = categories.get(eventType.category) ?? [];
      existing.push(eventType);
      categories.set(eventType.category, existing);
    });

    this.eventTypesByCategory.set(categories);
    this.eventTypes.set(eventTypes);
  }

  reloadEndpoints(): void {
    this.loadEndpoints();
  }

  createEndpoint(): void {
    this.editingEndpoint.set(null);
    this.isDialogVisible.set(true);
  }

  editEndpoint(endpoint: WebhookEndpointWithSubscriptions): void {
    this.editingEndpoint.set(endpoint);
    this.isDialogVisible.set(true);
  }

  readonly handleDialogSave = async (data: {
    name: string;
    url: string;
    enabled: boolean;
    retry: boolean;
    eventTypeIds: string[];
    lockVersion?: number;
  }): Promise<{ secret?: string } | void> => {
    const editing = this.editingEndpoint();
    const isEditMode = editing !== null;

    // Get all available webhook types from catalog (already loaded)
    const availableEventTypes = this.eventTypes();
    if (availableEventTypes.length === 0) {
      this.actionError.set(this.translateService.instant('SETTINGS_WEBHOOKS.ERROR.CATALOG_NOT_LOADED'));
      throw new Error('Webhook catalog is not loaded');
    }

    const subscriptions: string[] = data.eventTypeIds;

    this.isLoading.set(true);
    try {
      if (isEditMode && editing) {
        const updateRequest: UpdateWebhookEndpoint = {
          displayName: data.name,
          endpointUrl: data.url,
          signatureScheme: UpdateWebhookEndpointSignatureSchemeEnum.HmacSha256,
          enabled: data.enabled,
          retry: data.retry,
          subscriptions,
          lockVersion: data.lockVersion ?? 0,
        };
        await firstValueFrom(
          from(
            this.webhookEndpointsApi.updateEndpoint({
              endpointId: editing.id,
              updateWebhookEndpoint: updateRequest,
            })
          )
        );
        this.closeDialog();
        this.loadEndpoints();
        return;
      }

      const createRequest: CreateWebhookEndpoint = {
        displayName: data.name,
        endpointUrl: data.url,
        signatureScheme: CreateWebhookEndpointSignatureSchemeEnum.HmacSha256,
        enabled: data.enabled,
        retry: data.retry,
        subscriptions,
      };
      const created = await firstValueFrom(
        from(
          this.webhookEndpointsApi.createEndpoint({
            createWebhookEndpoint: createRequest,
          })
        )
      );
      // Hold the dialog open so it can reveal the secret. The list is
      // refreshed when the user acknowledges via handleSecretAcknowledged().
      return { secret: created.secret };
    } catch (error: unknown) {
      this.actionError.set(this.translateService.instant('SETTINGS.ACTION_ERROR.SAVE_FAILED'));
      throw error;
    } finally {
      this.isLoading.set(false);
    }
  };

  handleSecretAcknowledged(): void {
    this.closeDialog();
    this.loadEndpoints();
  }

  closeDialog(): void {
    this.isDialogVisible.set(false);
    this.editingEndpoint.set(null);
  }

  deleteEndpoint(endpoint: WebhookEndpointWithSubscriptions): void {
    const translatedMessage = this.translateService.instant(
      'SETTINGS_WEBHOOKS.DELETE_CONFIRM_MESSAGE'
    );
    const name = endpoint.displayName !== '' ? endpoint.displayName : endpoint.endpointUrl;
    const message = translatedMessage.replace('{name}', name);

    this.confirmationService.confirm({
      message,
      header: this.translateService.instant('SETTINGS_WEBHOOKS.DELETE_CONFIRM_TITLE'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.isLoading.set(true);
        from(this.webhookEndpointsApi.deleteEndpoint({ endpointId: endpoint.id }))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.isLoading.set(false);
              this.loadEndpoints();
            },
            error: (_error: unknown) => {
              this.isLoading.set(false);
              this.actionError.set(
                this.translateService.instant('SETTINGS_WEBHOOKS.ERROR.DELETE_FAILED')
              );
            },
          });
      },
    });
  }

  testEndpoint(endpoint: WebhookEndpointWithSubscriptions): void {
    this.testingEndpointId.set(endpoint.id);

    const testRequest: SendTestWebhook = {
      endpointId: endpoint.id,
    };

    from(
      this.webhookTestControllerApi.triggerTest({
        sendTestWebhook: testRequest,
      })
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (health) => {
          this.testingEndpointId.set(null);

          const healthForEndpoint = {
            lastTestTriedAt:
              health.lastTestTriedAt instanceof Date
                ? health.lastTestTriedAt
                : new Date(health.lastTestTriedAt),
            testWasSuccessful: health.testWasSuccessful,
          };
          this.endpoints.set(
            this.endpoints().map((e) =>
              e.id === endpoint.id ? { ...e, health: healthForEndpoint } : e
            )
          );

          if (!health.testWasSuccessful) {
            this.actionError.set(this.translateService.instant('SETTINGS_WEBHOOKS.ERROR.TEST_FAILED'));
          }
        },
        error: () => {
          this.testingEndpointId.set(null);

          this.actionError.set(this.translateService.instant('SETTINGS_WEBHOOKS.ERROR.TEST_FAILED'));
        },
      });
  }

  private deriveTestHealthDisplay(
    endpoint: WebhookEndpointWithSubscriptions,
    locale: string
  ): TestHealthDisplay {
    if (endpoint.health == null) {
      return { status: null, tooltip: '' };
    }
    const status: 'success' | 'failed' | null = endpoint.health.testWasSuccessful
      ? 'success'
      : 'failed';
    const triedAt =
      endpoint.health.lastTestTriedAt instanceof Date
        ? endpoint.health.lastTestTriedAt
        : new Date(endpoint.health.lastTestTriedAt);
    const formatted = formatDate(triedAt, 'dd.MM.yyyy HH:mm', locale);
    const tooltip = this.translateService.instant('SETTINGS_WEBHOOKS.TEST_TRIED_AT', {
      timestamp: formatted,
    });
    return { status, tooltip };
  }

  private deriveEventTypesSummary(endpoint: WebhookEndpointWithSubscriptions): string {
    const count = endpoint.subscriptionIds?.length ?? 0;
    if (count === 0) {
      return this.translateService.instant('SETTINGS_WEBHOOKS.NO_EVENT_TYPES');
    }
    const invalidCount = endpoint.invalidSubscriptionIds?.length ?? 0;
    let summary = this.translateService
      .instant('SETTINGS_WEBHOOKS.EVENT_TYPES_COUNT')
      .replace('{count}', count.toString());
    if (invalidCount > 0) {
      const invalidSuffix = this.translateService
        .instant('SETTINGS_WEBHOOKS.INVALID_COUNT')
        .replace('{count}', invalidCount.toString());
      summary += ` (${invalidSuffix})`;
    }
    return summary;
  }

  copyEndpointId(endpoint: WebhookEndpointWithSubscriptions, event: Event): void {
    event.stopPropagation();
    navigator.clipboard
      .writeText(endpoint.id)
      .then(() => undefined)
      .catch(() => {
        this.actionError.set(this.translateService.instant('SETTINGS_WEBHOOKS.ERROR.COPY_FAILED'));
      });
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }
}
